'use client';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import TopNav from '@/components/layout/TopNav';
import MedicalDisclaimer from '@/components/ui/MedicalDisclaimer';
import { getDoctorById, doctors } from '@/data/doctors';
import { getDepartmentById, departments } from '@/data/departments';
import { hospitalConfig } from '@/data/hospitalConfig';
import { whatsAppMessages } from '@/data/hospitalConfig';
import WhatsAppButton from '@/components/ui/WhatsAppButton';
import { trackEvent } from '@/lib/analytics';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { MapPin } from 'lucide-react';

interface BookingFormData {
  patientName: string;
  patientPhone: string;
  consultationType: 'in-person' | 'video';
  notes?: string;
}

const TIME_SLOTS = [
  'slot-0900', 'slot-0930', 'slot-1000', 'slot-1030',
  'slot-1100', 'slot-1130', 'slot-1200', 'slot-1400',
  'slot-1430', 'slot-1500', 'slot-1530', 'slot-1600',
];

const TIME_SLOT_LABELS: Record<string, string> = {
  'slot-0900': '9:00 AM', 'slot-0930': '9:30 AM', 'slot-1000': '10:00 AM',
  'slot-1030': '10:30 AM', 'slot-1100': '11:00 AM', 'slot-1130': '11:30 AM',
  'slot-1200': '12:00 PM', 'slot-1400': '2:00 PM', 'slot-1430': '2:30 PM',
  'slot-1500': '3:00 PM', 'slot-1530': '3:30 PM', 'slot-1600': '4:00 PM',
};

function generateCalendarDays() {
  const today = new Date(2026, 7, 11); // Use fixed date matching current timestamp
  const days = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push({
      id: `day-${i}`,
      date: d,
      dayLabel: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      dateLabel: d.getDate().toString(),
      monthLabel: d.toLocaleDateString('en-IN', { month: 'short' }),
      isToday: i === 0,
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
    });
  }
  return days;
}

export default function AppointmentBookingContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { selectedCentre } = useSelectedCentre();
  const [visible, setVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [selectedDayId, setSelectedDayId] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calendarDays = generateCalendarDays();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<BookingFormData>({
    defaultValues: { consultationType: 'in-person' },
  });

  const consultationType = watch('consultationType');

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    const docId = searchParams.get('doctorId') ?? '';
    const deptId = searchParams.get('departmentId') ?? '';
    if (docId) setSelectedDoctorId(docId);
    if (deptId) setSelectedDepartmentId(deptId);
    trackEvent({ event: 'booking_started', doctorId: docId, departmentId: deptId });
    return () => clearTimeout(t);
  }, [searchParams]);

  const selectedDoctor = selectedDoctorId ? getDoctorById(selectedDoctorId) : null;
  const selectedDepartment = selectedDepartmentId
    ? getDepartmentById(selectedDepartmentId)
    : selectedDoctor
    ? getDepartmentById(selectedDoctor.department)
    : null;

  const selectedDay = calendarDays.find((d) => d.id === selectedDayId);

  // BACKEND INTEGRATION POINT: Replace this handler with a fetch() to your booking API
  const onSubmit = async (data: BookingFormData) => {
    if (!selectedDayId || !selectedTimeSlot) {
      toast.error('Please select a date and time slot before confirming.');
      return;
    }
    setIsSubmitting(true);
    trackEvent({ event: 'booking_completed', doctorId: selectedDoctorId });

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 1200));

    setIsSubmitting(false);
    toast.success('Appointment request submitted! We will contact you to confirm.');
    router.push('/confirmation');
  };

  return (
    <main
      className="gradient-bg min-h-screen flex flex-col pb-20 md:pb-8"
      role="main"
      aria-label="Book a consultation at All Is Well Hospital"
    >
      <TopNav showBackButton backHref="/care-recommendation-screen" backLabel="Recommendation" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div
          className={`w-full max-w-2xl transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Header */}
          <div className="mb-8">
            <p className="section-label mb-2">ALL IS WELL HOSPITAL</p>
            <h1 className="text-display-md text-white font-bold">YOUR VISIT</h1>
            {selectedCentre && (
              <div className="flex items-center gap-2 mt-3 text-xs text-primary/70">
                <MapPin size={11} />
                <span className="font-semibold tracking-wide">{selectedCentre.name}</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Appointment booking form">
            {/* Visit summary */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Visit Summary
              </h2>
              <div className="flex flex-col gap-4">
                {/* Doctor selector */}
                <div>
                  <label
                    htmlFor="doctor-select"
                    className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block"
                  >
                    Doctor
                  </label>
                  {selectedDoctor ? (
                    <div className="flex items-center gap-3 bg-muted rounded-xl p-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: selectedDoctor.avatarColor }}
                        aria-hidden="true"
                      >
                        {selectedDoctor.initials}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-foreground">{selectedDoctor.name}</p>
                        <p className="text-xs text-muted-foreground">{selectedDoctor.specialty}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedDoctorId('')}
                        className="ml-auto text-muted-foreground hover:text-foreground transition-colors text-lg"
                        aria-label="Change doctor"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <select
                      id="doctor-select"
                      value={selectedDoctorId}
                      onChange={(e) => setSelectedDoctorId(e.target.value)}
                      className="input-field"
                      aria-label="Select a doctor"
                    >
                      <option value="">Select a doctor (optional)</option>
                      {doctors.map((doc) => (
                        <option key={`select-doc-${doc.id}`} value={doc.id}>
                          {doc.name} — {doc.specialty}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Department */}
                <div>
                  <label
                    htmlFor="dept-select"
                    className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block"
                  >
                    Department
                  </label>
                  {selectedDepartment ? (
                    <div className="flex items-center gap-3 bg-muted rounded-xl p-3">
                      <span className="text-xl" aria-hidden="true">{selectedDepartment.icon}</span>
                      <p className="font-semibold text-sm text-foreground">{selectedDepartment.name}</p>
                    </div>
                  ) : (
                    <select
                      id="dept-select"
                      value={selectedDepartmentId}
                      onChange={(e) => setSelectedDepartmentId(e.target.value)}
                      className="input-field"
                      aria-label="Select a department"
                    >
                      <option value="">Select a department</option>
                      {departments.map((dept) => (
                        <option key={`select-dept-${dept.id}`} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Hospital */}
                <div className="flex items-start gap-3 bg-muted rounded-xl p-3">
                  <span className="text-xl" aria-hidden="true">🏥</span>
                  <div>
                    <p className="font-semibold text-sm text-foreground">{hospitalConfig.name}</p>
                    <p className="text-xs text-muted-foreground">{hospitalConfig.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultation type */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Consultation Type
              </h2>
              <div
                className="grid grid-cols-2 gap-3"
                role="radiogroup"
                aria-label="Select consultation type"
              >
                {(['in-person', 'video'] as const).map((type) => (
                  <button
                    key={`consult-type-${type}`}
                    type="button"
                    onClick={() => setValue('consultationType', type)}
                    className={`rounded-xl p-4 border transition-all duration-200 text-sm font-semibold ${
                      consultationType === type
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-muted-foreground hover:border-primary/50'
                    }`}
                    role="radio"
                    aria-checked={consultationType === type}
                    aria-label={type === 'in-person' ? 'In-person consultation' : 'Video consultation'}
                  >
                    {type === 'in-person' ? '🏥 In-Person' : '💻 Video'}
                  </button>
                ))}
              </div>
            </div>

            {/* Date picker */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Select Date
              </h2>
              <div
                className="grid grid-cols-7 gap-2"
                role="radiogroup"
                aria-label="Select appointment date"
              >
                {calendarDays.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => setSelectedDayId(day.id)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all duration-200 ${
                      selectedDayId === day.id
                        ? 'border-primary bg-primary/10'
                        : day.isWeekend
                        ? 'border-border bg-muted/50 opacity-60' :'border-border bg-muted hover:border-primary/50'
                    }`}
                    role="radio"
                    aria-checked={selectedDayId === day.id}
                    aria-label={`${day.dayLabel} ${day.dateLabel} ${day.monthLabel}${day.isWeekend ? ' — reduced hours' : ''}`}
                    disabled={day.isToday}
                  >
                    <span
                      className={`text-xs font-medium ${
                        selectedDayId === day.id ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      {day.dayLabel}
                    </span>
                    <span
                      className={`text-sm font-bold font-tabular ${
                        selectedDayId === day.id ? 'text-primary' : 'text-foreground'
                      }`}
                    >
                      {day.dateLabel}
                    </span>
                    <span
                      className={`text-xs ${
                        selectedDayId === day.id ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      {day.monthLabel}
                    </span>
                  </button>
                ))}
              </div>
              {selectedDay && (
                <p className="text-xs text-primary mt-3 font-medium">
                  Selected: {selectedDay.dayLabel}, {selectedDay.dateLabel} {selectedDay.monthLabel}
                  {selectedDay.isWeekend ? ' — Weekend hours: 9AM–6PM' : ' — Weekday hours: 8AM–8PM'}
                </p>
              )}
            </div>

            {/* Time slots */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Select Time
              </h2>
              <div
                className="grid grid-cols-3 sm:grid-cols-4 gap-2"
                role="radiogroup"
                aria-label="Select appointment time"
              >
                {TIME_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setSelectedTimeSlot(slot)}
                    className={`time-slot ${selectedTimeSlot === slot ? 'selected' : ''}`}
                    role="radio"
                    aria-checked={selectedTimeSlot === slot}
                    aria-label={`Time slot: ${TIME_SLOT_LABELS[slot]}`}
                  >
                    {TIME_SLOT_LABELS[slot]}
                  </button>
                ))}
              </div>
            </div>

            {/* Patient details */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <h2 className="text-xs font-semibold text-muted-foreground tracking-widest uppercase mb-4">
                Your Details
              </h2>
              <div className="flex flex-col gap-4">
                {/* Name */}
                <div>
                  <label
                    htmlFor="patient-name"
                    className="text-sm font-medium text-foreground mb-1.5 block"
                  >
                    Full Name <span className="text-red-400" aria-hidden="true">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    As it appears on your ID document
                  </p>
                  <input
                    id="patient-name"
                    type="text"
                    placeholder="Enter your full name"
                    className="input-field"
                    aria-required="true"
                    aria-describedby={errors.patientName ? 'name-error' : undefined}
                    {...register('patientName', {
                      required: 'Please enter your full name',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' },
                    })}
                  />
                  {errors.patientName && (
                    <p id="name-error" className="text-red-400 text-xs mt-1.5" role="alert">
                      {errors.patientName.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="patient-phone"
                    className="text-sm font-medium text-foreground mb-1.5 block"
                  >
                    Mobile Number <span className="text-red-400" aria-hidden="true">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    We will contact you at this number to confirm your appointment
                  </p>
                  <input
                    id="patient-phone"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    className="input-field"
                    aria-required="true"
                    aria-describedby={errors.patientPhone ? 'phone-error' : undefined}
                    {...register('patientPhone', {
                      required: 'Please enter your mobile number',
                      pattern: {
                        value: /^[+]?[\d\s\-()]{8,15}$/,
                        message: 'Please enter a valid mobile number',
                      },
                    })}
                  />
                  {errors.patientPhone && (
                    <p id="phone-error" className="text-red-400 text-xs mt-1.5" role="alert">
                      {errors.patientPhone.message}
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label
                    htmlFor="patient-notes"
                    className="text-sm font-medium text-foreground mb-1.5 block"
                  >
                    Additional Notes
                    <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Brief description of your concern to help the care team prepare
                  </p>
                  <textarea
                    id="patient-notes"
                    placeholder="e.g. I have been experiencing chest discomfort for 3 days..."
                    rows={3}
                    className="input-field resize-none"
                    aria-label="Additional notes for your appointment"
                    {...register('notes')}
                  />
                </div>
              </div>
            </div>

            {/* Prototype notice */}
            <div
              className="border border-amber-800/50 bg-amber-950/20 rounded-xl p-4 mb-6"
              role="note"
              aria-label="Appointment confirmation notice"
            >
              <div className="flex items-start gap-3">
                <span className="text-amber-400 text-lg flex-shrink-0" aria-hidden="true">ℹ️</span>
                <div>
                  <p className="text-sm font-semibold text-amber-300 mb-1">
                    Appointment Confirmation
                  </p>
                  <p className="text-xs text-amber-200/80 leading-relaxed">
                    Your appointment request will be reviewed and confirmed by our team. We will
                    contact you at the number provided within 2–4 hours during hospital hours.
                    This is a request, not a confirmed booking.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full text-sm tracking-widest mb-4 relative"
              aria-label="Confirm appointment request"
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-3">
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Submitting Request...
                </span>
              ) : (
                'CONFIRM APPOINTMENT REQUEST →'
              )}
            </button>

            {/* Alternative contact */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <WhatsAppButton
                message={whatsAppMessages.appointment(
                  selectedDoctor?.name ?? 'a doctor',
                  selectedCentre?.shortName,
                )}
                label="BOOK VIA WHATSAPP"
                variant="inline"
                className="flex-1 py-3 text-sm"
                source="appointment-booking"
              />
              <a
                href={hospitalConfig.phoneTel}
                onClick={() => trackEvent({ event: 'call_clicked', source: 'booking' })}
                className="btn-ghost-white flex-1 text-sm"
                aria-label={`Call hospital: ${hospitalConfig.phone}`}
              >
                📞 CALL TO BOOK
              </a>
            </div>

            <MedicalDisclaimer />
          </form>
        </div>
      </div>
    </main>
  );
}